/**
 * The walk group's review round (lane C0-POSE-FIX; research docs/research/C0_POSES.md §4). The stride itself (core.ts
 * `walk`: feet, lift, bob, arm swing) and each body's bearing (rig/skeleton.ts `BEARINGS`: a man composed, a woman
 * graceful, an elder aged, a child lively) stay; laid over them, once the walk is approved in the studio (until then
 * only the studio's "new" side shows them):
 * - the weight passes over the standing foot: the hips (and everything above) shift toward the planted leg as the
 *   other swings, more for a woman, least for an elder;
 * - the shoulders turn against the stride with the arm swing (the right shoulder comes forward with the right arm);
 * - the forearm bends up on the forward swing (rig/skeleton.ts `fore`);
 * - an elder walks with the upper back rounded and the head forward (`Rig.hunch`).
 * All in skeleton terms (joints and shifts), so they carry over to any proportions.
 */
import type { BodyShape } from '../rig/skeleton';
import type { Rig } from './kit';

/** The walking poses (a stride's frames, the stop/start steps, putting a weapon away in stride). */
export const WALKS: ReadonlySet<string> = new Set(['walk', 'stepA', 'stepB', 'walkSheathe', 'walkUnsheathe']);

/** An elder's rounded back while walking (design px). */
export const ELDER_WALK_HUNCH = 3;

/**
 * How far the hips stand over the planted foot this frame, toward the right (A) side positive, in design px: the
 * lifted foot is the free one (foot 1 is the left, B; foot 2 the right, A).
 */
export function walkShift(rig: Pick<Rig, 'l1' | 'l2'>, body: BodyShape): number {
  const amount = body.age === 'elder' ? 0.7 : body.age === 'child' ? 0.8 : body.female ? 2 : 1;
  // The left foot up: the weight on the right; eased in with the lift (the walk lifts a foot about 2.5 px).
  return amount * Math.max(-1, Math.min(1, (rig.l1 - rig.l2) / 2.5));
}

/** The shoulders' turn against the stride (as `TWIST`: positive, the right shoulder forward). */
export function walkTwist(rig: Pick<Rig, 'armSwing'>): number {
  return Math.max(-3, Math.min(3, rig.armSwing * 1.0));
}

/** The running poses (the qinggong run; review round R3). */
export const RUNS: ReadonlySet<string> = new Set(['sprint']);

/**
 * The run's review round, laid over core.ts `sprint` once approved: the head pushed forward into the run, eyes level;
 * a woman's run lighter and a little more upright (POSING_RIGGING §5.3, wire-fu staging: the body laid into the
 * flight, the sleeves streaming).
 */
export function runPolish(rig: Rig, body: BodyShape): void {
  rig.headDx += 1;
  if (body.female) rig.tilt -= 3;
}

/** How far the hips stand over the planted foot in the run (toward the right, A, positive); the run lifts ~7 px. */
export function runShift(rig: Pick<Rig, 'l1' | 'l2'>, body: BodyShape): number {
  const amount = body.female ? 0.9 : 0.6;
  return amount * Math.max(-1, Math.min(1, (rig.l1 - rig.l2) / 7));
}

/**
 * The walk's per-body polish (review R2 notes): a woman's steps shorter, her hem kicking out on the stride and
 * trailing behind (the cloth follows `sway`); an elder as hunched as his idles, the trunk pitched a little, shorter
 * steps; everyone's body dropping a pixel at contact (both feet down).
 */
export function walkPolish(rig: Rig, body: BodyShape, ph: number, stride = 1): void {
  // The stride's feet unrounded (core.ts walks `gait` rounds to whole pixels, which made the planted foot's slide
  // uneven frame to frame; review R2 read it as a limp): the same gait, smooth, scaled by the bearing.
  const g = smoothGait((ph % 14) / 14, 6, 0.6, 2.5, 1);
  rig.f1 = g.f1 * stride;
  rig.f2 = g.f2 * stride;
  rig.l1 = g.l1 * stride;
  rig.l2 = g.l2 * stride;
  // Putting the weapon away or drawing it in stride: the arm passes the ready grip, not the raised guard, so the
  // blade does not cross the face head-on.
  if (rig.arm === 'guard') rig.arm = 'readyBlade';
  if (rig.armTo === 'guard') rig.armTo = 'readyBlade';
  if (body.female && body.age !== 'elder') {
    rig.f1 *= 0.8;
    rig.f2 *= 0.8;
    rig.sway += 0.9 * Math.cos((ph / 14) * Math.PI * 4);
  }
  if (body.age === 'elder') {
    rig.f1 *= 0.85;
    rig.f2 *= 0.85;
    rig.tilt += 3;
  }
  if (rig.l1 < 0.05 && rig.l2 < 0.05) rig.o += 1;
}

/** kit.ts `gait` without the rounding: both feet's forward offset and lift at `t` (0–1 of the stride). */
function smoothGait(t: number, reach: number, stance: number, lift: number, kick: number) {
  const leg = (p: number): [number, number] => {
    const q0 = ((p % 1) + 1) % 1;
    if (q0 < stance) return [reach - (2 * reach * q0) / stance, 0];
    const q = (q0 - stance) / (1 - stance);
    return [
      -reach + 2 * reach * (0.5 - 0.5 * Math.cos(Math.PI * q)),
      lift * Math.sin(Math.PI * Math.pow(q, kick)),
    ];
  };
  const [a, la] = leg(t);
  const [b, lb] = leg(t + 0.5);
  return { f1: b, l1: lb, f2: a, l2: la };
}
